// frontend/src/components/admin/Administrateurs.jsx
// ============================================================================
// ADMINISTRATEURS DU CENTRE
// ----------------------------------------------------------------------------
// Les comptes qui ouvrent le CIH. Creation avec mot de passe provisoire montre
// UNE fois, desactivation, et remise a zero de la double authentification pour
// quelqu'un qui a perdu son telephone.
//
// Le serveur refuse de laisser quelqu'un se couper lui-meme, et refuse de
// desactiver le dernier compte actif. L'ecran le dit avant d'essayer.
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import {
  UserCog, RefreshCw, UserPlus, ShieldCheck, ShieldOff,
  Smartphone, Copy, Check, X, AlertTriangle
} from 'lucide-react';
import superAdminApi from '../../services/superAdminApi';
import { useTheme } from '../../contexts/ThemeContext';

const champStyle = (t) => ({
  background: t.bgInput, border: `1px solid ${t.border}`, borderRadius: 10,
  padding: '9px 12px', fontSize: 13, color: t.text, outline: 'none', fontFamily: 'inherit'
});

const Administrateurs = () => {
  const t = useTheme();
  const [comptes, setComptes] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [message, setMessage] = useState(null);

  const [formulaireOuvert, setFormulaireOuvert] = useState(false);
  const [nom, setNom] = useState('');
  const [email, setEmail] = useState('');
  const [creation, setCreation] = useState(false);
  const [nouveau, setNouveau] = useState(null);
  const [copie, setCopie] = useState(false);

  const charger = useCallback(async () => {
    setChargement(true);
    setErreur(null);
    try {
      const r = await superAdminApi.administrateurs.liste();
      setComptes(r.data || []);
    } catch (e) {
      setErreur(e.message || 'Lecture impossible');
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  const creer = async (e) => {
    e.preventDefault();
    setCreation(true);
    setMessage(null);
    try {
      const r = await superAdminApi.administrateurs.creer({ nom, email });
      setNouveau(r.data);
      setNom('');
      setEmail('');
      setFormulaireOuvert(false);
      await charger();
    } catch (err) {
      setMessage(err.message || 'Création impossible');
    } finally {
      setCreation(false);
    }
  };

  const changerStatut = async (compte, actif) => {
    setMessage(null);
    try {
      const r = await superAdminApi.administrateurs.statut(compte.id, actif);
      setMessage(r.message);
      await charger();
    } catch (err) {
      setMessage(err.message || 'Action impossible');
    }
  };

  const reinitialiserDoubleAuth = async (compte) => {
    setMessage(null);
    try {
      const r = await superAdminApi.administrateurs.reinitialiserDoubleAuth(compte.id);
      setMessage(`${compte.email} : ${r.message}`);
      await charger();
    } catch (err) {
      setMessage(err.message || 'Action impossible');
    }
  };

  const actifs = comptes.filter((c) => c.status === 'active').length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
        <div>
          <h2 style={{ fontSize: 20, fontWeight: 800, color: t.text, margin: 0,
            display: 'flex', alignItems: 'center', gap: 9 }}>
            <UserCog size={20} style={{ color: t.primary }} />
            Administrateurs du centre
          </h2>
          <p style={{ fontSize: 13, color: t.textMuted, margin: '4px 0 0' }}>
            {comptes.length} compte(s), {actifs} actif(s). Ce sont les seuls à pouvoir ouvrir le CIH.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button type="button" onClick={() => setFormulaireOuvert((o) => !o)}
            style={{ background: t.primary, border: `1px solid ${t.primary}`, borderRadius: 10,
              padding: '9px 14px', fontSize: 13, fontWeight: 600, color: '#fff', cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 8, fontFamily: 'inherit' }}>
            <UserPlus size={14} />
            Nouveau compte
          </button>
          <button type="button" onClick={charger}
            style={{ ...champStyle(t), fontWeight: 600, cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <RefreshCw size={14} style={{ animation: chargement ? 'spin 1s linear infinite' : 'none' }} />
            Actualiser
          </button>
        </div>
      </div>

      {formulaireOuvert && (
        <form onSubmit={creer}
          style={{ background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 14,
            padding: 16, display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: t.textMuted }}>Nom</span>
            <input value={nom} onChange={(e) => setNom(e.target.value)} required
              placeholder="Prénom Nom" style={{ ...champStyle(t), width: 220 }} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: t.textMuted }}>Adresse e-mail</span>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
              placeholder="prenom@horapilot.com" style={{ ...champStyle(t), width: 280 }} />
          </label>
          <button type="submit" disabled={creation}
            style={{ background: t.primary, border: 0, borderRadius: 10, padding: '10px 18px',
              fontSize: 13, fontWeight: 700, color: '#fff', cursor: 'pointer', fontFamily: 'inherit' }}>
            {creation ? 'Création…' : 'Créer le compte'}
          </button>
          <p style={{ margin: 0, fontSize: 12, color: t.textMuted, flexBasis: '100%' }}>
            Un mot de passe provisoire sera affiché une seule fois. Le compte devra le changer
            à sa première connexion, puis configurer Google Authenticator.
          </p>
        </form>
      )}

      {nouveau && (
        <div style={{ background: t.successSoft, border: `1px solid ${t.success}40`, borderRadius: 14,
          padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: t.successText }}>
            Compte créé : {nouveau.email}
          </p>
          <p style={{ margin: 0, fontSize: 13, color: t.textSecondary }}>
            Mot de passe provisoire, à remettre en main propre. Il ne sera plus jamais affiché,
            et il n&rsquo;est écrit dans aucun journal.
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <code style={{ fontSize: 16, fontWeight: 700, letterSpacing: '0.04em', color: t.text,
              background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 8,
              padding: '10px 14px', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>
              {nouveau.provisoire}
            </code>
            <button type="button"
              onClick={() => {
                navigator.clipboard?.writeText(nouveau.provisoire);
                setCopie(true);
                setTimeout(() => setCopie(false), 2000);
              }}
              style={{ ...champStyle(t), cursor: 'pointer', display: 'inline-flex',
                alignItems: 'center', gap: 7, fontWeight: 600 }}>
              {copie ? <Check size={14} /> : <Copy size={14} />}
              {copie ? 'Copié' : 'Copier'}
            </button>
            <button type="button" onClick={() => setNouveau(null)}
              style={{ ...champStyle(t), cursor: 'pointer', display: 'inline-flex',
                alignItems: 'center', gap: 7, fontWeight: 600 }}>
              <X size={14} />
              J&rsquo;ai noté
            </button>
          </div>
        </div>
      )}

      {message && (
        <div style={{ background: t.bgInput, border: `1px solid ${t.border}`, borderRadius: 12,
          padding: '12px 16px', fontSize: 13, color: t.textSecondary }}>{message}</div>
      )}

      {erreur && (
        <div style={{ background: t.dangerSoft, border: `1px solid ${t.danger}30`, borderRadius: 14,
          padding: '14px 18px', fontSize: 14, color: t.dangerText }}>{erreur}</div>
      )}

      <div style={{ background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 14, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: t.bgTableHeader }}>
              {['Compte', 'État', 'Double authentification', 'Dernière connexion', 'Actions'].map((titre) => (
                <th key={titre} style={{ textAlign: 'left', padding: '11px 16px', fontSize: 11,
                  fontWeight: 700, color: t.textMuted, textTransform: 'uppercase',
                  letterSpacing: '0.5px', borderBottom: `1px solid ${t.border}` }}>{titre}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {comptes.map((c) => {
              const actif = c.status === 'active';
              const dernier = actif && actifs === 1;
              return (
                <tr key={c.id} style={{ borderBottom: `1px solid ${t.borderLight}` }}>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: t.text }}>{c.name}</span>
                    <span style={{ display: 'block', fontSize: 12, color: t.textMuted }}>{c.email}</span>
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{ fontSize: 12, fontWeight: 700, padding: '4px 10px', borderRadius: 99,
                      background: actif ? t.successSoft : t.bgInput,
                      color: actif ? t.successText : t.textMuted }}>
                      {actif ? 'actif' : 'désactivé'}
                    </span>
                    {c.mot_de_passe_provisoire && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, marginLeft: 8,
                        fontSize: 12, color: t.warningText }}>
                        <AlertTriangle size={12} /> mot de passe provisoire
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: 13 }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7,
                      color: c.double_auth ? t.successText : t.dangerText, fontWeight: 600 }}>
                      {c.double_auth ? <ShieldCheck size={14} /> : <ShieldOff size={14} />}
                      {c.double_auth ? 'configurée' : c.cle_creee ? 'clé créée, jamais confirmée' : 'absente'}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: 13, color: t.textSecondary }}>
                    {c.derniere_connexion || 'jamais'}
                    {c.last_ip && (
                      <span style={{ display: 'block', fontSize: 12, color: t.textMuted }}>{c.last_ip}</span>
                    )}
                    {c.echecs_30j > 0 && (
                      <span style={{ display: 'block', fontSize: 12, color: t.warningText }}>
                        {c.echecs_30j} échec(s) sur 30 jours
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <button type="button" onClick={() => reinitialiserDoubleAuth(c)}
                        style={{ ...champStyle(t), padding: '6px 11px', fontSize: 12, fontWeight: 600,
                          cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <Smartphone size={12} />
                        Téléphone perdu
                      </button>
                      <button type="button" onClick={() => changerStatut(c, !actif)} disabled={dernier}
                        title={dernier ? 'Le dernier compte actif ne se désactive pas' : undefined}
                        style={{ ...champStyle(t), padding: '6px 11px', fontSize: 12, fontWeight: 600,
                          cursor: dernier ? 'not-allowed' : 'pointer', opacity: dernier ? 0.5 : 1,
                          color: actif ? t.dangerText : t.successText }}>
                        {actif ? 'Désactiver' : 'Réactiver'}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p style={{ fontSize: 12, color: t.textMuted, margin: 0, lineHeight: 1.6 }}>
        Chacun de ces gestes est inscrit au journal des actions privilégiées. « Téléphone perdu »
        efface la clé Google Authenticator : le compte en reconfigure une à sa prochaine connexion.
        Pour votre propre compte, le serveur refuse — c&rsquo;est le meilleur moyen de se
        retrouver dehors.
      </p>
    </div>
  );
};

export default Administrateurs;
